import type { RoboticMowersSummaryItem } from '@fleet-bffs/api-contract';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import * as roboticMowerUpdateActions from '~/ducks/asset-updates/actions';
import { defaultState } from '../data/RoboticMowers';
import { findIndex, calculateFootprint, compareFootprints } from '../utils/utils';
import * as roboticMowersActions from './actions';

type UpdateAssetStatusAction = PayloadAction<
  Omit<ReturnType<typeof roboticMowerUpdateActions.updateAssetStatus>['payload'], 'status'> & {
    status: RoboticMowersSummaryItem['status'];
  }
>;

const slice = createSlice({
  name: 'robotic-mowers',
  initialState: defaultState,
  reducers: {
    clear: () => {
      return defaultState;
    },
    setIsLoading: (state, action: PayloadAction<{ isLoading: boolean }>) => {
      state.isLoading = action.payload.isLoading;
    },
    setItems: (state, action: PayloadAction<{ items: RoboticMowersSummaryItem[] }>) => {
      state.items = action.payload.items;
      state.footprintsItems = calculateFootprint(state.items);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      roboticMowerUpdateActions.updateAssetStatus.type,
      (state, action: UpdateAssetStatusAction) => {
        const index = findIndex(state.items, action.payload.assetId);
        if (index === -1) {
          return;
        }
        state.items[index].status = action.payload.status;

        if (!compareFootprints(state.footprintsItems ?? [], state.items) && !state.isLoading) {
          state.footprintsChangedTimestamp = Date.now();
        }
        state.footprintsItems = calculateFootprint(state.items);
      }
    );

    builder.addCase(
      roboticMowersActions.updateRoboticMowersMissionsProgress.type,
      (
        state,
        action: ReturnType<typeof roboticMowersActions.updateRoboticMowersMissionsProgress>
      ) => {
        for (const item of action.payload.items) {
          const index = findIndex(state.items, item.iprId);
          if (index === -1) {
            continue;
          }
          for (const mission of item.missions) {
            const missionIndex = state.items[index].missions.findIndex(
              (m) => m.missionId == mission.missionId
            );
            if (missionIndex === -1) {
              continue;
            }
            state.items[index].missions[missionIndex].progress = mission.progress;
            state.items[index].missions[missionIndex].current = mission.current;
          }
        }
      }
    );
  },
});

export const { clear, setIsLoading, setItems } = slice.actions;
export default slice.reducer;
